// =============================================================================
// pages/Settings.js — Phase 82 (sidebar layout redesign, from Figma export)
//
// The main settings hub. Left sidebar lists 8 sections; the right pane renders
// the active section. Section state is driven by ?section= URL query param so
// deep links from other parts of the app work (e.g. /settings?section=family).
//
// The old flat card layout has been replaced. Existing deep-link routes
// (/settings/billing, /settings/family, /settings/life-profile) still work
// unchanged and are handled by their own dedicated pages — this hub is a
// discoverable superset entry point.
//
// Design principles preserved from the Figma export:
//   • Sidebar collapses to a horizontal, scrollable tab-row on mobile.
//   • Every interactive element has a data-testid.
//   • Dark mode auto-swaps via CSS tokens (bg-card, text-charcoal, etc.).
// =============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut } from 'lucide-react';

import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import usePageTitle from '@/hooks/usePageTitle';
import { PrimaryButton } from '@/components/settings/primitives';
import SectionErrorBoundary from '@/components/SectionErrorBoundary';

import PlanSection    from '@/components/settings/PlanSection';
import FamilySection  from '@/components/settings/FamilySection';
import ProfileSection from '@/components/settings/ProfileSection';
import RegionSection  from '@/components/settings/RegionSection';
import EmailSection   from '@/components/settings/EmailSection';
import AIUsageSection from '@/components/settings/AIUsageSection';
import HelpSection    from '@/components/settings/HelpSection';
import { LifeProfileEmbed } from './SettingsLifeProfile';

// ─── Nav config ─────────────────────────────────────────────────────────────
// Each nav item's icon is a hand-tuned inline SVG matched to the Figma export
// (kept as JSX so they inherit currentColor for the active/inactive state).
const NAV_ITEMS = [
  {
    id: 'plan', label: 'Plan',
    icon: (
      <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="1" y="3" width="13" height="9" rx="1.5" /><path d="M1 6h13M4 9.5h2" />
      </svg>
    ),
  },
  {
    id: 'family', label: 'Family & Sharing',
    icon: (
      <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="5.5" cy="4.5" r="2" /><circle cx="10.5" cy="5.5" r="1.5" />
        <path d="M1 13c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" /><path d="M11 12c1 0 3-.5 3-2.5" />
      </svg>
    ),
  },
  {
    id: 'profile', label: 'Profile',
    href: '/profile',
    icon: (
      <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="7.5" cy="5" r="2.5" /><path d="M2 13c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      </svg>
    ),
  },
  {
    id: 'region', label: 'Region & Currency',
    icon: (
      <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="7.5" cy="7.5" r="5.5" /><path d="M7.5 2C6 4 5 5.5 5 7.5s1 3.5 2.5 5.5" />
        <path d="M7.5 2C9 4 10 5.5 10 7.5s-1 3.5-2.5 5.5" /><line x1="2" y1="7.5" x2="13" y2="7.5" />
      </svg>
    ),
  },
  {
    id: 'email', label: 'Email Preferences',
    icon: (
      <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="1" y="3" width="13" height="9" rx="1" /><polyline points="1,3.5 7.5,9 14,3.5" />
      </svg>
    ),
  },
  {
    id: 'ai-usage', label: 'AI Usage',
    icon: (
      <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="4" width="11" height="8" rx="1" /><path d="M5 4V2.5M7.5 4V2M10 4V2.5" /><path d="M5 9h1.5M8.5 9H10M5 11h5" />
      </svg>
    ),
  },
  {
    id: 'help', label: 'Help & Orientation',
    icon: (
      <svg width="15" height="15" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="7.5" cy="7.5" r="5.5" /><path d="M5.5 5.5a2 2 0 0 1 4 0c0 1.5-2 2-2 3.5" />
        <circle cx="7.5" cy="11" r="0.5" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
];

const VALID_SECTIONS = new Set(NAV_ITEMS.map((n) => n.id));

// ─── Theme toggle (moved out of a card into the sidebar footer) ──────────────
function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <div
      className="mx-3 mt-2 mb-3 rounded-xl border border-border p-1 flex items-center gap-1 bg-offwhite"
      data-testid="settings-theme-toggle"
    >
      <button
        type="button"
        onClick={() => setTheme('light')}
        className={`flex-1 rounded-lg py-1.5 text-xs font-medium transition-colors ${
          theme === 'light'
            ? 'bg-card text-charcoal shadow-sm'
            : 'text-muted-foreground hover:text-charcoal'
        }`}
        data-testid="settings-theme-light"
      >
        Light
      </button>
      <button
        type="button"
        onClick={() => setTheme('dark')}
        className={`flex-1 rounded-lg py-1.5 text-xs font-medium transition-colors ${
          theme === 'dark'
            ? 'bg-card text-charcoal shadow-sm'
            : 'text-muted-foreground hover:text-charcoal'
        }`}
        data-testid="settings-theme-dark"
      >
        Dark
      </button>
    </div>
  );
}

export default function Settings() {
  usePageTitle('Settings');
  const { signOut } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // v3.7.7 Phase 84.31 — legacy deep-links now live under /profile.
  // Silently redirect old ?section=profile and ?section=life-profile
  // URLs so bookmarks and inbound links resolve to the new hub.
  useEffect(() => {
    const q = searchParams.get('section');
    if (q === 'profile')      nav('/profile', { replace: true });
    if (q === 'life-profile') nav('/profile?tab=life', { replace: true });
  }, []);

  // Resolve initial section from ?section= URL param, defaulting to 'plan'.
  const paramSection = searchParams.get('section');
  const initialSection = VALID_SECTIONS.has(paramSection) ? paramSection : 'plan';
  const [active, setActive] = useState(initialSection);

  // When the query param changes externally (deep links, back/forward),
  // reflect it in local state. Setting local state via the same setter
  // avoids double-update loops.
  useEffect(() => {
    const q = searchParams.get('section');
    if (q && VALID_SECTIONS.has(q) && q !== active) setActive(q);
  }, [searchParams, active]);

  // When user clicks a nav item, keep the URL in sync so refresh/back works.
  const changeSection = (id) => {
    setActive(id);
    const next = new URLSearchParams(searchParams);
    if (id === 'plan') next.delete('section');
    else next.set('section', id);
    setSearchParams(next, { replace: true });
  };

  const handleLogout = async () => {
    await signOut();
    toast.success('Signed out.');
    nav('/login');
  };

  // Which section component to render on the right. Each section is wrapped
  // in a SectionErrorBoundary so a failure in one (bad network response,
  // schema drift, etc.) doesn't take down the entire Settings hub — the
  // sidebar keeps working and the user can retry just the broken pane.
  const content = useMemo(() => {
    const map = {
      'plan':         { label: 'Plan',         node: <PlanSection /> },
      'family':       { label: 'Family',       node: <FamilySection /> },
      'profile':      { label: 'Profile',      node: <ProfileSection /> },
      'life-profile': { label: 'Life Profile', node: <LifeProfileEmbed showTitle /> },
      'region':       { label: 'Region',       node: <RegionSection /> },
      'email':        { label: 'Email',        node: <EmailSection /> },
      'ai-usage':     { label: 'AI Usage',     node: <AIUsageSection /> },
      'help':         { label: 'Help',         node: <HelpSection /> },
    };
    const entry = map[active] || map.plan;
    return (
      <SectionErrorBoundary label={entry.label}>
        {entry.node}
      </SectionErrorBoundary>
    );
  }, [active]);

  return (
    <div
      className="min-h-full flex flex-col md:flex-row -mx-4 sm:-mx-6 -my-6"
      data-testid="settings-page"
    >
      {/* Sidebar */}
      <aside
        className="md:w-64 flex-shrink-0 border-b md:border-b-0 md:border-r border-border bg-card"
        data-testid="settings-sidebar"
      >
        <div className="px-6 py-6 md:py-8 border-b border-border/60 flex items-center justify-between md:block gap-4">
          <h1
            className="font-display text-xl md:text-2xl font-medium text-charcoal"
            style={{ letterSpacing: '-0.01em' }}
          >
            Settings
          </h1>
        </div>

        <nav
          className="px-3 py-4 flex md:flex-col gap-0.5 overflow-x-auto md:overflow-x-visible"
          style={{ scrollbarWidth: 'none' }}
          aria-label="Settings sections"
        >
          {NAV_ITEMS.map((item) => {
            const isActive = active === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  // v3.7.7 Phase 84.31 — nav items with an `href` navigate
                  // externally instead of swapping the embedded pane. This
                  // is how the unified /profile hub is reached from
                  // Settings without duplicating its shell here.
                  if (item.href) nav(item.href);
                  else changeSection(item.id);
                }}
                data-testid={`settings-nav-${item.id}`}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all text-left flex-shrink-0 md:w-full whitespace-nowrap ${
                  isActive
                    ? 'bg-evergreen/10 text-evergreen font-medium'
                    : 'text-muted-foreground hover:text-charcoal hover:bg-offwhite'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className={isActive ? 'text-evergreen' : 'text-muted-foreground/70'}>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Sidebar footer: theme + sign out (desktop only) */}
        <div className="hidden md:block border-t border-border/60 pt-3 mt-2 pb-4">
          <ThemeToggle />
          <button
            type="button"
            onClick={handleLogout}
            className="mx-3 mt-1 flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-charcoal hover:bg-offwhite transition-colors w-[calc(100%-1.5rem)]"
            data-testid="settings-logout-button"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Content pane */}
      <main
        className="flex-1 px-4 sm:px-6 lg:px-10 py-6 md:py-8 max-w-3xl w-full"
        data-testid="settings-content"
      >
        {content}

        {/* Mobile-only footer actions */}
        <div className="md:hidden mt-8 space-y-3">
          <ThemeToggle />
          <PrimaryButton variant="ghost" onClick={handleLogout} testId="settings-logout-button-mobile">
            <span className="inline-flex items-center gap-2"><LogOut className="h-4 w-4" /> Sign out</span>
          </PrimaryButton>
        </div>
      </main>
    </div>
  );
}
